/**
 * Shared quick-view variant picker (snippets/quick-view.liquid, a native
 * <dialog> included once in layout/theme.liquid). Opens for product cards
 * whose "Select options" button carries
 * `data-quick-view-trigger data-product-handle="<handle>"`
 * (snippets/product-card.liquid - shown instead of the plain quick-add
 * button when a product has more than one option and every option has more
 * than one value, so which variant is "right" is genuinely ambiguous).
 *
 * Fetches `/products/<handle>.js` on open (cached per handle so reopening
 * the same product doesn't refetch) and builds one pill fieldset per
 * option, defaulting to Shopify's own "selected or first available
 * variant" (the first `available: true` variant, or the product's first
 * variant if none are) rather than the first value of each option picked
 * independently - the latter can land on a combination nothing was ever
 * stocked in (e.g. Colour defaults to Red, Size defaults to S, but Red only
 * ships in M/L). Adds the matching variant to the cart through the same
 * Section Rendering flow as assets/quick-add.js and assets/product-form.js.
 */
const quickViewProductCache = new Map();

function formatQuickViewMoney(cents, format) {
  const amount = (cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return (format || '${{amount}}').replace('{{amount}}', amount).replace('{{ amount }}', amount);
}

function initQuickView() {
  const dialog = document.querySelector('[data-quick-view]');
  if (!dialog) return;

  const image = dialog.querySelector('[data-quick-view-image]');
  const title = dialog.querySelector('[data-quick-view-title]');
  const price = dialog.querySelector('[data-quick-view-price]');
  const status = dialog.querySelector('[data-quick-view-status]');
  const form = dialog.querySelector('[data-quick-view-form]');
  const optionsTarget = dialog.querySelector('[data-quick-view-options]');
  const notice = dialog.querySelector('[data-quick-view-notice]');
  const addButton = dialog.querySelector('[data-quick-view-add]');
  const moneyFormat = dialog.dataset.moneyFormat || '${{amount}}';
  const cartAddUrl = dialog.dataset.cartAddUrl;

  let currentProduct = null;
  let currentVariant = null;

  function setStatus(text) {
    status.hidden = !text;
    status.textContent = text || '';
  }

  function setNotice(text) {
    notice.hidden = !text;
    notice.textContent = text || '';
  }

  function renderVariant() {
    const selected = Array.from(optionsTarget.querySelectorAll('[data-option-position]:checked'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value);

    currentVariant = currentProduct.variants.find((variant) =>
      variant.options.every((value, index) => value === selected[index])
    );

    if (currentVariant) {
      price.textContent = formatQuickViewMoney(currentVariant.price, moneyFormat);
      addButton.disabled = !currentVariant.available;
      addButton.textContent = currentVariant.available ? addButton.dataset.addText : addButton.dataset.soldOutText;
      setNotice('');
    } else {
      addButton.disabled = true;
      addButton.textContent = addButton.dataset.addText;
      setNotice(notice.dataset.unavailableText);
    }
  }

  function pickDefaultVariant(product) {
    return product.variants.find((variant) => variant.available) || product.variants[0];
  }

  function buildOptions(product, defaultVariant) {
    optionsTarget.innerHTML = '';

    product.options.forEach((option) => {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'quick-view__option';

      const legend = document.createElement('legend');
      legend.textContent = option.name;
      fieldset.append(legend);

      const valuesWrap = document.createElement('div');
      valuesWrap.className = 'quick-view__option-values';
      const defaultValue = defaultVariant?.options?.[option.position - 1];

      option.values.forEach((value, valueIndex) => {
        const id = `QuickViewOption-${option.position}-${valueIndex}`;

        const input = document.createElement('input');
        input.type = 'radio';
        input.id = id;
        input.name = `quick-view-option-${option.position}`;
        input.value = value;
        input.dataset.optionPosition = String(option.position);
        // Falls back to the first value if the default variant is missing
        // its options array (shouldn't happen, but keeps a fieldset from
        // rendering with nothing checked).
        if (defaultValue ? value === defaultValue : valueIndex === 0) input.checked = true;

        const label = document.createElement('label');
        label.setAttribute('for', id);
        label.textContent = value;

        valuesWrap.append(input, label);
      });

      fieldset.append(valuesWrap);
      optionsTarget.append(fieldset);
    });
  }

  async function open(handle) {
    currentProduct = null;
    currentVariant = null;
    title.textContent = '';
    price.textContent = '';
    image.removeAttribute('src');
    optionsTarget.innerHTML = '';
    addButton.disabled = true;
    setNotice('');
    setStatus(dialog.dataset.loadingText);
    dialog.showModal();

    try {
      let product = quickViewProductCache.get(handle);
      if (!product) {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) throw new Error('not-found');
        product = await response.json();
        quickViewProductCache.set(handle, product);
      }

      currentProduct = product;
      title.textContent = product.title;
      image.src = product.featured_image || product.images?.[0] || '';
      image.alt = product.title;
      buildOptions(product, pickDefaultVariant(product));
      renderVariant();
      setStatus('');
    } catch (error) {
      setStatus(dialog.dataset.loadErrorText);
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-quick-view-trigger]');
    if (!trigger) return;
    const handle = trigger.dataset.productHandle;
    if (!handle) return;
    open(handle);
  });

  optionsTarget.addEventListener('change', (event) => {
    if (event.target.matches('[data-option-position]')) renderVariant();
  });

  dialog.querySelector('[data-quick-view-close]')?.addEventListener('click', () => dialog.close());

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!currentVariant || !cartAddUrl) return;

    const submitText = addButton.textContent;
    addButton.disabled = true;
    addButton.setAttribute('data-loading', '');

    try {
      const sectionIds = Array.from(document.querySelectorAll('[data-section-id]'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      const body = new FormData();
      body.append('id', currentVariant.id);
      body.append('quantity', '1');
      if (sectionIds.length) body.append('sections', sectionIds.join(','));

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || addButton.dataset.errorText);

      if (window.vellumCart && data.sections) {
        window.vellumCart.applySections(data.sections, { forceDrawerOpen: true });
        window.dispatchEvent(new CustomEvent('vellum:cart-updated'));
      } else {
        window.sessionStorage.setItem('vellum:openCartDrawer', '1');
        window.location.reload();
        return;
      }

      dialog.close();
      addButton.disabled = false;
      addButton.removeAttribute('data-loading');
      addButton.textContent = submitText;
    } catch (error) {
      setNotice(error.message || addButton.dataset.errorText);
      addButton.disabled = false;
      addButton.removeAttribute('data-loading');
      addButton.textContent = submitText;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuickView);
} else {
  initQuickView();
}
